import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BarChart3, Printer, RefreshCw, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Shell } from "@/components/Shell";
import { listBusinesses, getType } from "@/data/business";
import { getSections } from "@/data/questionnaire";
import { contribute, fetchBenchmark, statsFor, insights, MIN_REAL } from "@/data/benchmark";
import { loadAll, formatDate } from "@/lib/storage";

const Bar = ({ label, pct, mine }) => (
  <div className="grid gap-1" data-testid={mine ? "bench-bar-mine" : undefined}>
    <div className="flex justify-between gap-3 text-sm"><span className={mine ? "font-semibold text-[#2C221E]" : "text-[#5C4D46]"}>{label}{mine && <span className="ml-2 text-xs uppercase tracking-wider text-[#C85A32]">la tua risposta</span>}</span><span className="text-[#7A6B63] flex-none">{pct}%</span></div>
    <div className="h-2.5 rounded-full bg-[#F3ECE1] overflow-hidden"><div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${pct}%`, background: mine ? "#C85A32" : "#B9A98F" }} /></div>
  </div>
);

const Source = ({ st }) => (
  <p className="text-xs text-[#8A7970] mt-3" data-testid="bench-source">
    {st.fonte === "reale" ? `Dati reali: ${st.n} compilazioni di questa tipologia` : st.fonte === "riferimento" ? `Valori di riferimento indicativi del settore${st.n ? ` (solo ${st.n} compilazioni reali, servono almeno ${MIN_REAL})` : ""}` : `Solo ${st.n} compilazioni reali: dato ancora poco affidabile`}
  </p>
);

const QuestionCard = ({ q, value, st }) => {
  const list = Array.isArray(value) ? value : [value];
  return (
    <div className="card p-5" data-testid={`bench-q-${q.id}`}>
      <p className="font-semibold text-[#2C221E] leading-snug">{q.text}</p>
      {st.opzioni && (
        <div className="grid gap-3 mt-4">
          {(q.options || Object.keys(st.opzioni)).map((o) => <Bar key={o} label={o} pct={st.opzioni[o] ?? 0} mine={list.includes(o)} />)}
          {q.options && list.filter((o) => !q.options.includes(o)).length > 0 && <p className="text-xs text-[#8A7970]">Risposta non tra le opzioni attuali: {list.join(", ")}</p>}
        </div>
      )}
      {st.media !== undefined && q.type === "scale" && (
        <div className="mt-4 grid gap-2">
          <div className="flex justify-between text-sm"><span className="font-semibold text-[#C85A32]">Tu: {value} / {q.max}</span><span className="text-[#5C4D46]">Media: {st.media} / {q.max}</span></div>
          <div className="relative h-2.5 rounded-full bg-[#F3ECE1]">
            <div className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-[#B9A98F] border-2 border-white" style={{ left: `calc(${((st.media - 1) / (q.max - 1)) * 100}% - 8px)` }} title="Media" />
            <div className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-[#C85A32] border-2 border-white" style={{ left: `calc(${((value - 1) / (q.max - 1)) * 100}% - 8px)` }} title="Tu" />
          </div>
        </div>
      )}
      {st.mediana !== undefined && q.type === "number" && (
        <p className="mt-3 text-sm"><span className="font-semibold text-[#C85A32]">Tu: {value} {q.unit}</span> <span className="text-[#5C4D46]">· mediana del settore: {st.mediana} {q.unit} (media {st.media})</span></p>
      )}
      <Source st={st} />
    </div>
  );
};

export default function BenchmarkPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const businesses = useMemo(listBusinesses, []);
  const subs = useMemo(() => loadAll().filter((s) => s.status === "completed"), []);
  const [subId, setSubId] = useState(id || subs[0]?.id || "");
  const sub = subs.find((s) => s.id === subId);
  const [bench, setBench] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async (sync) => {
    if (!sub) return;
    setBusy(true);
    setError("");
    try {
      if (sync) {
        const n = await contribute(loadAll());
        toast.success(`Archivio aggiornato: ${n} compilazioni inviate in forma anonima`);
      }
      setBench(await fetchBenchmark(sub.businessType, sub.id));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { contribute(loadAll()).catch(() => 0).then(() => load(false)); }, [subId]); // eslint-disable-line react-hooks/exhaustive-deps

  const biz = sub && (businesses.find((b) => b.id === (sub.businessId || "default")) || { type: sub.businessType, name: sub.businessName });
  const sections = sub ? getSections(sub.businessType, biz) : [];
  const rows = sections.flatMap((s) => s.questions.map((q) => ({ q, value: sub.answers[q.id], st: statsFor(bench, q.id), section: s.title }))).filter((r) => r.st && r.value !== undefined && r.value !== "" && r.q.type !== "text");
  const ins = sub && bench ? insights(sections, sub.answers, bench) : null;

  return (
    <Shell header={<button className="btn btn-ghost !min-h-[44px] text-sm" onClick={() => navigate("/")} data-testid="bench-back-btn"><ArrowLeft size={16} /> Elenco</button>}>
      <div className="fade-up">
        <div className="flex items-center gap-3"><BarChart3 className="text-[#2D4030]" size={24} /><p className="eyebrow">Benchmark di settore</p></div>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-[#2C221E] mt-2">Come si colloca rispetto alle altre</h1>
        <p className="text-[#5C4D46] mt-3 max-w-xl">Le risposte a scelta di ogni compilazione completata vengono salvate in forma anonima (niente nomi né testi liberi). Qui confronti un cliente con tutte le attività della stessa tipologia; finché i dati reali sono pochi, si usano valori di riferimento indicativi.</p>

        <div className="card p-5 mt-8 grid sm:grid-cols-[1fr_auto] gap-3 items-end no-print" data-testid="bench-picker">
          <label className="grid gap-2">
            <span className="text-sm font-semibold text-[#2C221E]">Compilazione da confrontare</span>
            {subs.length === 0 ? <p className="text-sm text-[#A83232]" data-testid="bench-nosub">Nessuna compilazione completata su questo tablet.</p> : (
              <select className="field" value={subId} onChange={(e) => setSubId(e.target.value)} data-testid="bench-sub-select">
                {subs.map((s) => <option key={s.id} value={s.id}>{s.businessName || "Attività"} · {s.respondent || "Senza nome"} · {formatDate(s.createdAt)}</option>)}
              </select>
            )}
          </label>
          <div className="flex gap-2">
            <button className="btn btn-ghost !min-h-[48px]" onClick={() => load(true)} disabled={busy || !sub} data-testid="bench-sync-btn"><RefreshCw size={16} className={busy ? "animate-spin" : ""} /> Aggiorna archivio</button>
            <button className="btn btn-ghost !min-h-[48px]" onClick={() => window.print()} disabled={!bench} data-testid="bench-print-btn"><Printer size={16} /> Stampa</button>
          </div>
        </div>
        {error && <p className="text-sm text-[#A83232] mt-3" data-testid="bench-error">{error}</p>}

        {sub && bench && (
          <>
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mt-8">
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#382E2B]">{sub.businessName || "Attività"} <span className="text-[#7A6B63] font-sans text-base">· {getType(sub.businessType).label}</span></h2>
              <span className="text-sm text-[#7A6B63]" data-testid="bench-count">{bench.n} altre compilazioni reali di questa tipologia</span>
            </div>

            <div className="grid md:grid-cols-2 gap-5 mt-5">
              <div className="rounded-2xl p-5 border bg-[#FBF1EA] border-[#C85A3255]" data-testid="bench-distingue">
                <div className="flex items-center gap-2 text-[#C85A32]"><Sparkles size={18} /><p className="font-serif text-xl font-semibold">Dove si distingue</p></div>
                {ins.distingue.length === 0 ? <p className="text-sm italic text-[#B0A096] mt-3">Nessuno scostamento rilevante dal settore</p> : <ul className="grid gap-2 mt-3">{ins.distingue.map((t, i) => <li key={i} className="text-sm text-[#2C221E] flex gap-2"><span className="text-[#C85A32]">•</span><span>{t}</span></li>)}</ul>}
              </div>
              <div className="rounded-2xl p-5 border bg-[#F1F4EC] border-[#6E835855]" data-testid="bench-linea">
                <div className="flex items-center gap-2 text-[#6E8358]"><CheckCircle2 size={18} /><p className="font-serif text-xl font-semibold">In linea con il settore</p></div>
                {ins.linea.length === 0 ? <p className="text-sm italic text-[#B0A096] mt-3">Nessuna risposta allineata alla maggioranza</p> : <ul className="grid gap-2 mt-3">{ins.linea.map((t, i) => <li key={i} className="text-sm text-[#2C221E] flex gap-2"><span className="text-[#6E8358]">•</span><span>{t}</span></li>)}</ul>}
              </div>
            </div>

            <div className="grid gap-4 mt-8" data-testid="bench-questions">
              {rows.length === 0 && <p className="text-[#7A6B63]">Non ci sono ancora dati di confronto per le domande di questa compilazione.</p>}
              {rows.map((r) => <QuestionCard key={r.q.id} {...r} />)}
            </div>
          </>
        )}
        {sub && !bench && busy && <p className="text-[#7A6B63] mt-6" data-testid="bench-loading">Carico il confronto…</p>}
      </div>
    </Shell>
  );
}
